import React, { Component } from "react";
import { CartItemCaptionType, CartItemImageType, CartItemType, ProductClipType, UseCartReturnType } from "../types/index";
import withUseCart from "../pages/cart/withUseCart.tsx";
import UploadPreviewFile from "./upload-preview.tsx";
import sortablejs from "react-sortablejs";
import { CaptionFieldsForm } from './caption-fields-form';
const { ReactSortable } = sortablejs;

interface ItemInterface {
    id: string | number;
    selected?: boolean;
    chosen?: boolean;
    filtered?: boolean;
}

type UploadCardItemImageType = CartItemImageType & ({
    file?: File;
    progress?: number;
    error?: string;
    preview?: string;
    position: number;
} & ItemInterface)

type UploadCardItemType = CartItemType & {
    images: UploadCardItemImageType[]
};

interface UploadImagesProps {
    notabs?: boolean;
    popup?: boolean;
    cartItem: UploadCardItemType;
    product: ProductClipType;
    onPopupCloseClick: () => void;
    onDone: (cartItem: CartItemType) => void;
}
class UploadImages extends Component<UploadImagesProps & UseCartReturnType> {

    state: {
        selectedTab: string;
        selectedCount: number;
        isDragOver: boolean;
        captions: CartItemCaptionType[];
    };
    uploadedFiles: UploadCardItemImageType[];
    cartItem: UploadCardItemType;
    product: ProductClipType;
    maxPhotos?: number;

    constructor(props: UploadImagesProps & UseCartReturnType) {
        super(props);

        this.cartItem = props.cartItem;
        this.product = props.product;
        

        this.uploadedFiles = this.cartItem.images.map((v, index) => ({
            ...v,
            position: index,
            preview: `/api/media/${v.image}?s=s`,
        }));

        this.state = {
            selectedTab: "local",
            selectedCount: this.uploadedFiles.filter((v) => !!v.id).length,
            isDragOver: false,
            captions: this.cartItem.captions || [],
        };
        this.maxPhotos = this.product.clipProperties.imageCount + (this.cartItem.additionalPhotoCount || 0);
    }

    componentDidUpdate(prevProps: UploadImagesProps & UseCartReturnType) {
        if (prevProps.cartItem !== this.props.cartItem) {
            this.uploadedFiles = this.props.cartItem.images.map((v, index) => ({
                ...v,
                position: index,
                preview: `/api/media/${v.image}?s=s`,
            }));
            this.updateSelectedCount();
        }

    }

    updateSelectedCount = () => {
        const selectedCount = this.uploadedFiles.filter((v) => !!v.id).length;
        this.setState({ selectedCount });
    };
    generateUploadId(file: File): string {
        // Encode the filename to handle non-Latin characters
       const encodedName = encodeURIComponent(file.name);
       // Add timestamp for uniqueness
       const timestamp = Date.now();
       // Create a hash-like string that's safe for HTTP headers
       return `file-${timestamp}-${encodedName.replace(/%/g, '')}`;
    }


    handleFileUpload = (files: FileList | null) => {
        if (!files) return;

        const newFiles = Array.from(files).map((file, index) => ({
            file,
            position: index + this.uploadedFiles.length
        })).slice(0, Math.max(Number(this.maxPhotos) - this.uploadedFiles.length, 0)) as UploadCardItemImageType[];

        this.uploadedFiles = [...this.uploadedFiles, ...newFiles];
        this.processUploadedFiles()
    };

    processUploadedFiles = () => {
        this.uploadedFiles.forEach((props, index) => {

            const { file, progress } = props;
            if (file && progress === undefined) {
                this.handleFileProcessing(file, index);
            } else if (progress === 100 && this.cartItem && file) {
                this.uploadFile(file, index);
            }
        });
    };

    handleFileProcessing = (file: File, index: number) => {
        const img = new Image();
        const objectURL = URL.createObjectURL(file);

        img.onload = () => {
            const { width, height } = img;

            if (width > 300) {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    const scale = 300 / width;
                    canvas.width = 300;
                    canvas.height = height * scale;
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    const resizedDataUrl = canvas.toDataURL("image/jpeg");
                    this.updateUploadedFile(index, { preview: resizedDataUrl, progress: 2 });
                }
            } else {
                this.updateUploadedFile(index, { preview: objectURL, progress: 2 });
            }

            URL.revokeObjectURL(objectURL);
            this.uploadFile(file, index);
        };

        img.onerror = () => {
            this.updateUploadedFile(index, { error: "Error loading image", file: undefined, progress: undefined });
        };

        img.src = objectURL;
    };


    uploadFile = (file: File, index: number) => {

        const { updateCartItemImage, cartItem } = this.props;
        if (file?.size > 2 * 1024 * 1024) {
            const uploadId = this.generateUploadId(file);
            this.uploadFileInChunks(file, uploadId, (progress) => {
                this.updateUploadedFile(index, { progress });
            })
                .then(() => {
                    updateCartItemImage({
                        cartItem,
                        position: index,
                        image: `{{${uploadId}}}`,
                        headers: new Headers({
                            'X-Chunk-ID': uploadId,
                            'x-chunk-name': encodeURIComponent(file.name),
                        }),
                        onError: (error) => {
                            console.error('Error occurred during upload:', error);
                            this.updateUploadedFile(index, { error: "Error while uploading", file: undefined, progress: undefined });
                        },
                        callback: (_updatedItem, updatedImage) => {
                            if (updatedImage) {

                                this.updateUploadedFile(index, {
                                    ...updatedImage,
                                    file: undefined,
                                    error: undefined,
                                    progress: undefined,
                                    position: index,
                                    preview: `/api/media/${updatedImage.image}?s=s`
                                });
                            }

                        }
                    })
                }).catch((error) => {
                    console.error('Error occurred during upload:', error);
                    this.updateUploadedFile(index, { error: "Error while uploading", file: undefined, progress: undefined });
                });
        } else {
            updateCartItemImage({
                cartItem,
                position: index,
                image: file,
                onError: (error) => {
                    console.error('Error occurred during upload:', error);
                    this.updateUploadedFile(index, { error: "Error while uploading", file: undefined, progress: undefined });
                },

                callback: (_updatedItem, updatedImage, progress) => {
                    if (progress !== undefined) {
                        this.updateUploadedFile(index, { progress });
                    }

                    if (updatedImage) {
                        this.updateUploadedFile(index, {
                            ...updatedImage,
                            file: undefined,
                            error: undefined,
                            progress: undefined,
                            position: index,
                            preview: `/api/media/${updatedImage.image}?s=s`
                        });
                    }

                }
            })
        }
    }

    async uploadFileInChunks(file: File, uploadId: string, onProgress?: (progress: number) => void): Promise<void> {
        const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const fileName = file.name;
        let totalProgress = 0;

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            // Get the start and end byte positions of the chunk
            const start = chunkIndex * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunkBlob = file.slice(start, end);

            // Calculate the base progress for completed chunks
            const baseProgress = (chunkIndex / totalChunks) * 100;

            await new Promise<void>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                let attempts = 0;
                const MAX_RETRIES = 3;

                if (attempts > 0 && onProgress) {
                    onProgress(baseProgress);
                }

                const attemptUpload = () => {
                    xhr.open('POST', '/api/media/upload', true);
                    xhr.setRequestHeader('X-Chunk-Index', String(chunkIndex));
                    xhr.setRequestHeader('x-chunk-length', String(totalChunks));
                    xhr.setRequestHeader('X-Chunk-Name', encodeURIComponent(fileName));
                    xhr.setRequestHeader('X-Chunk-ID', uploadId);
                    xhr.setRequestHeader('X-Chunk-Type', file.type);

                    xhr.upload.onprogress = (e) => {
                        if (e.lengthComputable && onProgress) {
                            // Calculate progress for current chunk
                            const chunkProgress = (e.loaded / e.total) * (100 / totalChunks);
                            totalProgress = baseProgress + chunkProgress;
                            onProgress(Math.min(totalProgress, 100));
                        }
                    };

                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                            const chunkStatus = xhr.getResponseHeader('X-Chunk-Status');
                            if (chunkStatus === 'complete') {
                                if (onProgress) onProgress(100);
                                resolve();
                                return;
                            }
                            resolve();
                        } else {
                            if (attempts < MAX_RETRIES) {
                                attempts++;
                                attemptUpload();
                            } else {
                                reject(new Error(`Failed to upload chunk ${chunkIndex + 1} after ${MAX_RETRIES} attempts.`));
                            }
                        }
                    };

                    xhr.onerror = () => {
                        if (attempts < MAX_RETRIES) {
                            attempts++;
                            attemptUpload();
                        } else {
                            reject(new Error(`Network error uploading chunk ${chunkIndex + 1} after ${MAX_RETRIES} attempts.`));
                        }
                    };

                    xhr.send(chunkBlob);
                };

                attemptUpload();
            });
        }
    }



    updateUploadedFile = (index: number, props: Partial<UploadCardItemImageType>) => {
        this.uploadedFiles = this.uploadedFiles.map((file, i) => (i === index ? { ...file, ...props } : file));
        this.updateSelectedCount()
        this.forceUpdate()
    };

    updatePositions = (updatedList: UploadCardItemImageType[]) => {
        const needToUpdate = updatedList.reduce((acc, { position }, i) => {
            if (acc) {
                return true;
            }
            return position !== i;
        }, false);

        if (needToUpdate) {
            this.uploadedFiles = updatedList;
            const { updateCartItemImagePositions } = this.props;
            const positions = updatedList.filter((v) => v.id).
                map((v, i) => ({ id: Number(v.id), position: i }));
            updateCartItemImagePositions({
                id: Number(this.cartItem.id),
                product: this.product.id
            }, positions, (updatedItem) => {
                this.uploadedFiles = updatedItem.images.map((v) => ({
                    ...v,
                    preview: `/api/media/${v.image}?s=s`
                }));
            });
        }
    };



    setSelectedTab = (tab: string) => {
        this.setState({ selectedTab: tab });
    };


    removeFile = (file: CartItemImageType) => {
        if (file.id && this.cartItem) {
            const { removeCartItemImage } = this.props;
            removeCartItemImage({
                cartItem: this.cartItem,
                id: file.id,
                callback: (updatedItem) => {
                    this.uploadedFiles = updatedItem.images.map((v) => ({
                        ...v,
                        preview: `/api/media/${v.image}?s=s`
                    }));
                    this.updatePositions(this.uploadedFiles);
                    this.updateSelectedCount()
                }
            });
        } else if (file.position !== undefined) {
            this.uploadedFiles = this.uploadedFiles.filter((v) => v.position !== file.position);
            this.uploadedFiles = this.uploadedFiles.map((v, i) => ({ ...v, position: i }));
            this.forceUpdate()
        }
    }

    handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files && files.length) {
            this.handleFileUpload(files);
        }
        this.setState({ isDragOver: false });
    };

    handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        this.setState({ isDragOver: true });
    };

    handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        this.setState({ isDragOver: false });
    };

    updateFormValues = () => {
        const { updateCartItem } = this.props;
        this.cartItem.captions = this.state.captions;
        this.cartItem.images = this.uploadedFiles.map(v => ({
            ...v,
            preview: `/api/media/${v.image}?s=s`,
        }));
        updateCartItem(this.cartItem, (cartItem) => {
            this.cartItem = cartItem;
        });
        this.props.onDone(this.cartItem);
    }

    hexToRgb(hex: string) {
        // Remove the hash (#) if it exists
        hex = hex.replace(/^#/, '');
        // Parse the red, green, and blue components
        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;

        return `${r}, ${g}, ${b}`;
    };

    render() {
        const { selectedTab, selectedCount, isDragOver } = this.state;
        const { notabs, popup = true, onPopupCloseClick } = this.props;
        const dropzoneHighlightStyle = isDragOver ? {
            backgroundColor: `rgba(${this.hexToRgb(getComputedStyle(document.documentElement).getPropertyValue('--primary'))}, 0.1)`,
            borderColor: `var(--primary)`,
        } : {};

        const uploading = this.uploadedFiles.filter(file => 
            file.progress !== undefined && 
            file.progress < 100 && 
            !file.error
        ).length > 0;

        return (
            <div
                className={`upload_popup js_popup upload ${notabs ? "upload_notabs" : ""} ${popup ? " m-active" : ""
                    }`}

            >
                {!notabs && (
                    <>
                        {/* Tabs for selecting upload source */}
                        <input
                            type="radio"
                            className="upload__radio"
                            name="upload_radio"
                            id="upload_radio_local"
                            checked={selectedTab === "local"}
                            onChange={() => this.setSelectedTab("local")}
                        />
                        <input
                            type="radio"
                            className="upload__radio"
                            name="upload_radio"
                            id="upload_radio_instagram"
                            checked={selectedTab === "instagram"}
                            onChange={() => this.setSelectedTab("instagram")}
                        />
                        <input
                            type="radio"
                            className="upload__radio"
                            name="upload_radio"
                            id="upload_radio_google"
                            checked={selectedTab === "google"}
                            onChange={() => this.setSelectedTab("google")}
                        />
                        <input
                            type="radio"
                            className="upload__radio"
                            name="upload_radio"
                            id="upload_radio_dropbox"
                            checked={selectedTab === "dropbox"}
                            onChange={() => this.setSelectedTab("dropbox")}
                        />
                    </>
                )}

                <div className="upload__inner inside-wrapper">
                    {!notabs && (
                        <div className="upload__left">
                            <ul className="upload__nav">
                                {[
                                    { id: "local", icon: "file", label: "Local Files" },
                                    { id: "instagram", icon: "instagram", label: "Instagram" },
                                    { id: "google", icon: "google_photos", label: "Google Photos" },
                                    { id: "dropbox", icon: "dropbox", label: "Dropbox" },
                                ].map((tab) => (
                                    <li className="upload__nav-item" key={tab.id}>
                                        <label
                                            className="upload__nav-trigger"
                                            htmlFor={`upload_radio_${tab.id}`}
                                        >
                                            <svg role="img" className="upload__nav-trigger-ico">
                                                <use href={`./img/sprite.svg#${tab.icon}`}></use>
                                            </svg>
                                            <span className="upload__nav-trigger-text">
                                                {tab.label}
                                            </span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="upload__center">
                        <div className="upload__body">
                            {selectedTab === "local" && (
                                <div className="upload__content dropzone">
                                    <div className="upload__content-title dropzone__title">
                                        Drag & Drop <br /> any files
                                    </div>
                                    <div className="upload__content-text dropzone__text">or</div>
                                    <div className="upload__content-btns">
                                        <div className="dropzone__holder">
                                            <button className="btn btn_alt btn_lg" type="button">
                                                UPLOAD FROM CAMERA ROLL OR PC
                                            </button>
                                            <div className="dropzone__box"
                                                style={dropzoneHighlightStyle}
                                                onDrop={this.handleDrop}
                                                onDragOver={this.handleDragOver}
                                                onDragEnter={this.handleDragEnter}
                                                onDragLeave={this.handleDragLeave}
                                            >
                                                <input
                                                    type="file"
                                                    className="dropzone__input"
                                                    multiple
                                                    onChange={(e) => this.handleFileUpload(e.target.files!)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {selectedTab === "instagram" && (
                                <div className="upload__content">
                                    <div className="upload__content-title">
                                        Upload a file from Instagram
                                    </div>
                                    <div className="upload__content-text">
                                        Get your photos from Instagram. <br />
                                        We play nice. You just need to login.
                                    </div>
                                    <div className="upload__content-btns">
                                        <button className="btn btn_alt btn_lg" type="button">
                                            CONNECT TO INSTAGRAM
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Add Google and Dropbox tabs in similar fashion */}
                        </div>

                        <div className="upload__bottom">

                        </div>
                    </div>


                    <CaptionFieldsForm
                        className="upload__right"
                        fields={this.product.captionFields}
                        initialCaptions={this.state.captions}
                        uploadedFilesCount={this.uploadedFiles.length}
                        onSubmit={(captions) => {
                            this.setState({ captions }, () => {
                                this.updateFormValues();
                            });
                        }}
                    >
                        <div className="upload__preview">
                            <div className="upload__preview-head">
                                <div className="upload__preview-title">Selection preview</div>
                                <p>
                                    Drag and drop the photos to arrange them in the order you want to
                                    see them in the video.
                                </p>
                            </div>
                            <div className="upload__preview-list-holder">
                                <ReactSortable
                                    className="upload__preview-list js_sortable"
                                    handle=".js_sortable_handle"
                                    animation={250}
                                    tag="div"
                                    list={this.uploadedFiles}
                                    setList={this.updatePositions}>
                                    {this.uploadedFiles.map((uploadable, index) => (
                                        <UploadPreviewFile
                                            key={index}
                                            index={index}
                                            removeFile={this.removeFile}
                                            {...uploadable}
                                            disableRemoveButton={uploading}
                                            hideMoveButton={uploading}
                                        />
                                    ))}
                                </ReactSortable>
                            </div>
                        </div>
                        <div className="upload__bottom">
                            <div className="upload__selected">
                                <div className="upload__selected-text">
                                    Selected {selectedCount} of {this.maxPhotos}
                                </div>
                                <div className="upload__selected-btns">
                                    <button className="btn btn_cta"
                                        disabled={uploading}
                                        type="submit"
                                    >
                                        {uploading ? 'Waiting for upload...' : 'Done'}
                                    </button>
                                </div>
                            </div>


                        </div>
                    </CaptionFieldsForm>


                    {popup && (
                        <button
                            onClick={onPopupCloseClick}
                            className="upload__close js_popup_close"
                            type="button"
                            aria-label="Close"
                            title="Close"
                        />
                    )}

                </div>
            </div>
        );
    }
}

export default withUseCart(UploadImages);
